import { O, pipe } from '@mobily/ts-belt';
import { Lambda } from 'common/types';
import { usePropertyRadioContext } from 'app/properties/components/SingleRadioProperty';
import { generateNodeId } from 'common/offline/generateNodeId';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';
import { useQueryClient } from 'react-query';
import { GetPropertyConnectionResponse, GetPropertyRadioConnectionResponse } from 'app/connection/api';
import { usePropertyContext } from '../../properties/components/SingleProperty';
import { createBundle, CREATE_BUNDLE } from '../api/createBundle';
import { createCableRoute, CREATE_CABLE_ROUTE } from '../api/createCableRoute';
import { createFireBreach, CREATE_FIRE_BREACH } from '../api/createFireBreach';
import { createODF, CREATE_ODF } from '../api/createODF';
import { createTechnicalODF, CREATE_TECHNICAL_ODF } from '../api/createTechnicalODF';
import { createRack, CREATE_RACK } from '../api/createRack';
import { createSwitch, CREATE_SWITCH } from '../api/createSwitch';
import { createTechnicalSwitch, CREATE_TECHNICAL_SWITCH } from '../api/createTechnicalSwitch';
import { createWifiDot, CREATE_WIFIDOT } from '../api/createWifiDot';
import { createTpOutlet, CREATE_TPOUTLET } from '../api/createTpOutlet';
import { createCrossConnectionPanel, CREATE_CROSS_CONNECTION_PANEL } from '../api/createCrossPanel';
import { createTechnicalEquipment, CREATE_TECHNICAL_EQUIPMENT } from '../api/createTechnicalEquipment';
import { createTrunk, CREATE_TRUNK } from '../api/createTrunk';
import { createBaseStation, CREATE_BASE_STATION } from '../api/createBaseStation';
import { createDropAntenna, CREATE_DROP_ANTENNA } from '../api/createDropAntenna';
import { createRadioUnit, CREATE_RADIO_UNIT } from '../api/createRadioUnit';
import { createInputAntenna, CREATE_INPUT_ANTENNA } from '../api/createInputAntenna';
import { createCombiner, CREATE_COMBINER } from '../api/createCombiner';
import { createCoupler, CREATE_COUPLER } from '../api/createCoupler';
import { createRepeater, CREATE_REPEATER } from '../api/createRepeater';
import { createSplitter, CREATE_SPLITTER } from '../api/createSplitter';
import { propertyConnectionKey } from './useGetPropertyConnections';
import { propertyRadioConnectionKey } from './useGetPropertyRadioConnections';

type SuccessErrorCallbacks = {
  onSuccess: Lambda<void, void>;
  onError: Lambda<void, void>;
};

// Map entity types to their GraphQL mutation names
// This handles special casing (e.g., 'odf' -> 'createODF', 'tpOutlet' -> 'createTpOutlet')
const getMutationName = (type: string): string => {
  const mutationNameMap: Record<string, string> = {
    odf: 'createODF',
    technicalOdf: 'createTechnicalODF',
    tpOutlet: 'createTpOutlet',
    wifiDot: 'createWifiDot',
    crossPanel: 'createCrossConnectionPanel',
  };

  return mutationNameMap[type] || `create${type.charAt(0).toUpperCase()}${type.slice(1)}`;
};

// Types that use BasicObjectInput with 'basicObjectDetails' key instead of '<type>Details'
const BASIC_OBJECT_TYPES = new Set(['rack', 'fireBreach', 'cableRoute']);

// Radio domain node types (use propertyRadioConnection cache key)
const RADIO_NODE_TYPES = new Set([
  'baseStation',
  'radioUnit',
  'inputAntenna',
  'dropAntenna',
  'combiner',
  'coupler',
  'splitter',
  'repeater',
]);

// Get the correct details key for the entity type
// Some types share the same input type (e.g., technicalOdf uses odfDetails)
const getDetailsKey = (type: string): string => {
  // Map types to their actual details key when it differs from the type name
  const detailsKeyMap: Record<string, string> = {
    technicalOdf: 'odfDetails',
    technicalSwitch: 'switchDetails',
    crossPanel: 'crossConnectionPanelDetails',
  };

  if (detailsKeyMap[type]) {
    return detailsKeyMap[type];
  }

  return BASIC_OBJECT_TYPES.has(type) ? 'basicObjectDetails' : `${type}Details`;
};

// Convert node details from mutation payload to NodeData format for optimistic updates
const createOptimisticNodeData = (details: any, type: string) => {
  return {
    id: details.id,
    text: details.text || null,
    model: type,
    group: '', // Not inside a premise group
    cfObject: {
      text: details.text,
      location: details.location,
      images: details.photosURLs || [],
      brand: details.brand,
      model: details.model,
      installedAt: details.installedAt,
      contactType: details.contactType,
      switchType: details.switchType,
      deviceType: details.deviceType,
      ip: details.incomingIP || details.ip,
      mac: details.mac,
      serialNumber: details.serialNumber,
    },
    portCount: details.connectionsCount || details.portCount,
    leftPortCount: details.leftPortCount,
    rightPortCount: details.rightPortCount,
  };
};

// Helper to prepare node details with generated ID and propertyId
const prepareNodeDetails = (details: any, propertyId: number, type: string) => {
  // Generate a client-side ID if not provided
  // This ensures nodes can be referenced by links/fibers even when created offline
  const nodeId = details.id || generateNodeId();
  const nodeDetails = {
    ...details,
    id: nodeId,
    type,
  };

  // Return in the format expected by the GraphQL mutation and batch API
  // The batch API expects: { input: { propertyId, <detailsKey>: {...} } }
  // Most types use '<type>Details' (e.g., trunkDetails, odfDetails)
  // Basic object types (rack, fireBreach, cableRoute) use 'basicObjectDetails'
  const detailsKey = getDetailsKey(type);
  return {
    input: {
      propertyId,
      [detailsKey]: nodeDetails,
    },
  };
};

const useCreateNode = (
  type: string,
  apiCallFn: Lambda<number, Lambda<any, Promise<unknown>>>,
  mutationDocument: string,
  { onSuccess, onError }: SuccessErrorCallbacks,
) => {
  const { id } = usePropertyContext();
  const { id: radioId } = usePropertyRadioContext();
  const queryClient = useQueryClient();

  const currentPropertyId = id || radioId;
  const propertyId = O.getWithDefault(currentPropertyId, 0);

  // Get the correct mutation name for the entity type
  const mutationName = getMutationName(type);

  // Determine which cache key to use based on node type
  const isRadioNode = RADIO_NODE_TYPES.has(type);
  const cacheKey = isRadioNode
    ? propertyRadioConnectionKey(currentPropertyId)
    : propertyConnectionKey(currentPropertyId);

  // Apply optimistic update to React Query cache
  const applyOptimisticUpdate = (optimisticData: unknown) => {
    const nodeData = optimisticData as ReturnType<typeof createOptimisticNodeData>;

    if (isRadioNode) {
      queryClient.setQueryData<GetPropertyRadioConnectionResponse | undefined>(cacheKey, (oldData) => {
        if (!oldData?.propertyById?.radioConnectionByRadioConnectionId?.serialized2) {
          return oldData;
        }
        return {
          ...oldData,
          propertyById: {
            ...oldData.propertyById,
            radioConnectionByRadioConnectionId: {
              ...oldData.propertyById.radioConnectionByRadioConnectionId,
              serialized2: {
                ...oldData.propertyById.radioConnectionByRadioConnectionId.serialized2,
                nodeDataArray: [
                  ...oldData.propertyById.radioConnectionByRadioConnectionId.serialized2.nodeDataArray,
                  nodeData,
                ],
              },
            },
          },
        };
      });
    } else {
      queryClient.setQueryData<GetPropertyConnectionResponse | undefined>(cacheKey, (oldData) => {
        // Create a new node data array with the optimistic node
        const existingNodes = oldData?.propertyById?.connectionByConnectionId?.serialized2?.nodeDataArray ?? [];
        const newNodeDataArray = [...existingNodes, nodeData];

        // If there's no existing data structure, create one
        if (!oldData?.propertyById?.connectionByConnectionId?.serialized2) {
          return {
            propertyById: {
              ...oldData?.propertyById,
              connectionByConnectionId: {
                ...oldData?.propertyById?.connectionByConnectionId,
                serialized2: {
                  nodeDataArray: newNodeDataArray,
                  linkDataArray: oldData?.propertyById?.connectionByConnectionId?.serialized2?.linkDataArray ?? [],
                },
              },
            },
          } as GetPropertyConnectionResponse;
        }

        return {
          ...oldData,
          propertyById: {
            ...oldData.propertyById,
            connectionByConnectionId: {
              ...oldData.propertyById.connectionByConnectionId,
              serialized2: {
                ...oldData.propertyById.connectionByConnectionId.serialized2,
                nodeDataArray: newNodeDataArray,
              },
            },
          },
        };
      });
    }
  };

  const offlineMutation = useOfflineMutation<unknown, unknown, any>({
    mutationFn: (preparedPayload: any) =>
      pipe(
        currentPropertyId,
        // The payload is already in { input: { propertyId, ...Details } } format
        // Extract the details part to pass to the API function
        O.map((propId) => {
          const detailsKey = getDetailsKey(type);
          const nodeDetails = preparedPayload.input[detailsKey];
          return apiCallFn(propId)(nodeDetails);
        }),
        O.getExn,
      ),
    mutationName,
    graphqlDocument: mutationDocument,
    entity: type,
    operationType: 'CREATE',
    propertyId,
    invalidateKeys: [
      ['propertyConnection', currentPropertyId],
      ['propertyRadioConnection', currentPropertyId],
    ],
    // Generate optimistic data from the payload for immediate UI update
    getOptimisticData: (preparedPayload: any) => {
      const detailsKey = getDetailsKey(type);
      const nodeDetails = preparedPayload.input[detailsKey];
      return createOptimisticNodeData(nodeDetails, type);
    },
    applyOptimisticUpdate,
    onSuccess: () => {
      onSuccess();
    },
    onError: () => {
      onError();
    },
    onOfflineQueued: () => {
      // Still call onSuccess when queued offline so UI updates
      onSuccess();
    },
  });

  // Return a wrapper that pre-processes variables before passing to mutation
  return {
    ...offlineMutation,
    mutate: (details: any, options?: any) => {
      const preparedPayload = prepareNodeDetails(details, propertyId, type);
      return offlineMutation.mutate(preparedPayload, options);
    },
    mutateAsync: (details: any, options?: any) => {
      const preparedPayload = prepareNodeDetails(details, propertyId, type);
      return offlineMutation.mutateAsync(preparedPayload, options);
    },
  };
};

export const useCreateBundle = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('bundle', createBundle, CREATE_BUNDLE, callbacks);
};

export const useCreateODF = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('odf', createODF, CREATE_ODF, callbacks);
};

export const useCreateTechnicalODF = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('technicalOdf', createTechnicalODF, CREATE_TECHNICAL_ODF, callbacks);
};

export const useCreateSwitch = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('switch', createSwitch, CREATE_SWITCH, callbacks);
};

export const useCreateTechnicalSwitch = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('technicalSwitch', createTechnicalSwitch, CREATE_TECHNICAL_SWITCH, callbacks);
};

export const useCreateWifiDot = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('wifiDot', createWifiDot, CREATE_WIFIDOT, callbacks);
};

export const useCreateTpOutlet = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('tpOutlet', createTpOutlet, CREATE_TPOUTLET, callbacks);
};

export const useCreateCrossPanel = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('crossPanel', createCrossConnectionPanel, CREATE_CROSS_CONNECTION_PANEL, callbacks);
};

export const useCreateTechnicalEquipment = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('technicalEquipment', createTechnicalEquipment, CREATE_TECHNICAL_EQUIPMENT, callbacks);
};

export const useCreateTrunk = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('trunk', createTrunk, CREATE_TRUNK, callbacks);
};

export const useCreateFireBreach = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('fireBreach', createFireBreach, CREATE_FIRE_BREACH, callbacks);
};

export const useCreateRack = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('rack', createRack, CREATE_RACK, callbacks);
};

export const useCreateCableRoute = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('cableRoute', createCableRoute, CREATE_CABLE_ROUTE, callbacks);
};

export const useCreateBaseStation = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('baseStation', createBaseStation, CREATE_BASE_STATION, callbacks);
};

export const useCreateDropAntenna = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('dropAntenna', createDropAntenna, CREATE_DROP_ANTENNA, callbacks);
};

export const useCreateRadioUnit = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('radioUnit', createRadioUnit, CREATE_RADIO_UNIT, callbacks);
};

export const useCreateInputAntenna = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('inputAntenna', createInputAntenna, CREATE_INPUT_ANTENNA, callbacks);
};

export const useCreateCombiner = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('combiner', createCombiner, CREATE_COMBINER, callbacks);
};

export const useCreateCoupler = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('coupler', createCoupler, CREATE_COUPLER, callbacks);
};

export const useCreateRepeater = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('repeater', createRepeater, CREATE_REPEATER, callbacks);
};

export const useCreateSplitter = (callbacks: SuccessErrorCallbacks) => {
  return useCreateNode('splitter', createSplitter, CREATE_SPLITTER, callbacks);
};
